import { useCallback, useEffect, useState } from 'react';
import { MOBILE_BREAKPOINT, useIsMobile } from '@/hooks/use-mobile';

interface Opcoes {
  /** Em tela de celular o painel começa (e fica) fechado. Padrão: true. */
  fecharNoCelular?: boolean;
}

const lerAberto = (chave: string) => {
  try {
    return localStorage.getItem(chave) !== '1';
  } catch {
    return true;
  }
};

/**
 * Painel lateral que o usuário recolhe/expande (paleta de formas do Fluxograma, "Adicionar" do Organograma).
 * No desktop a escolha fica salva no navegador (`chave` = '1' quando recolhido). Com `fecharNoCelular`, em tela
 * pequena o painel fica fechado; voltando ao desktop, volta à escolha salva.
 */
export function usePainelRecolhivel(chave: string, { fecharNoCelular = true }: Opcoes = {}) {
  // `useIsMobile` só sabe a largura depois do 1º efeito; a largura direta evita o "pisca" do 1º render.
  const isMobile = useIsMobile();
  const celular = fecharNoCelular && (isMobile || (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT));

  const [aberto, setAberto] = useState(() => !celular && lerAberto(chave));

  // Mudou de tamanho de tela (girar tablet, redimensionar janela): celular fecha; desktop volta à escolha salva.
  useEffect(() => {
    setAberto(celular ? false : lerAberto(chave));
  }, [celular, chave]);

  // Grava só a escolha feita no desktop (no celular o fechado é automático, não preferência).
  useEffect(() => {
    if (celular) return;
    try {
      localStorage.setItem(chave, aberto ? '0' : '1');
    } catch {
      // só conveniência
    }
  }, [aberto, celular, chave]);

  const alternar = useCallback(() => setAberto((v) => !v), []);

  return { aberto, setAberto, alternar, celular };
}
